import Panel from "sap/m/Panel";
import { MetadataOptions } from "sap/ui/core/Element";
import { AuditLogDto } from "../models/AuditLogDto";
import { GetController } from "../utils/CustomControlUtil";
import BaseController from "../pages/sharedControllers/BaseController";
import HBox from "sap/m/HBox";
import { ILogFormatter } from "./AuditLogFormatters/ILogFormatter";
import { WorkOrderLogFormatter } from "./AuditLogFormatters/WorkOrderLogFormatter";
import { BaseLogFormatter } from "./AuditLogFormatters/BaseLogFormatter";
import Label from "sap/m/Label";
import Icon from "sap/ui/core/Icon";
import { FormatDateTime } from "../utils/FormatUtil";
import { ServicePriceLogFormatter } from "./AuditLogFormatters/ServicePriceLogFormatter";
import { AuditLogTranslationKeys } from "../utils/AuditLogTranslationKeyUtil";
import { SpecificationLogFormatter } from "./AuditLogFormatters/SpecificationLogFormatter";
import { ContractLogFormatter } from "./AuditLogFormatters/ContractLogFormatter";

/**
 * This control is meant to handle all types of audit logs. It should be able to adapt any specific data, e.g. format dates, translate values, show colors for statuses, etc.
 * @namespace akademika.akamont.customControls
 */
export default class AuditLog extends Panel {
  static readonly metadata: MetadataOptions = {
    properties: {
      logData: { type: "object" }, // AuditLogDto from backend
    },
  };

  public setLogData(newLogData: AuditLogDto) {
    this.setProperty("logData", newLogData, true);
    this.updateLogDisplay(newLogData);
  }

  init(): void {
    super.init.apply(this);
    this.setBackgroundDesign("Solid");
  }

  static renderer = { apiVersion: 2 };

  private updateLogDisplay(logData: AuditLogDto) {
    const controller = GetController(this) as BaseController;
    this.removeAllContent();
    this.setHeaderText("");

    if (logData?.action) {
      let headerText = `${FormatDateTime(
        logData.timestamp
      )}: ${controller.translate(logData.action)}`;

      if (logData.userName) {
        headerText = `${logData.userName}, ${headerText}`;
      }
      this.setHeaderText(headerText);
    }

    const logFormatter = this.getLogFormatter(logData, controller);

    for (const logItem of logData.auditLogItems || []) {
      let labelTextKey =
        AuditLogTranslationKeys[logItem.fieldName] || logItem.fieldName;

      const fieldLabelControl = new Label({
        text: controller.translate(labelTextKey),
        width: "12em",
        wrapping: true,
      });

      const oldValueControl = new HBox({
        width: "50%",
        justifyContent: "End",
        items: [
          logFormatter.generateControlForValue(
            logItem.oldValue,
            logItem.fieldName
          ),
        ],
      });

      const newItems = [];
      if (logItem.fieldName === "attachments") {
        newItems.push(
          logFormatter.generateControlForValueObject(
            logItem.newValue,
            logItem.fieldName
          )
        )
      } else {
        newItems.push(
          logFormatter.generateControlForValue(
            logItem.newValue,
            logItem.fieldName
          )
        )
      }
      const newValueControl = new HBox({
        width: "50%",
        items: newItems,
      });

      this.addContent(
        new HBox({
          items: [
            fieldLabelControl,
            oldValueControl,
            this.createArrowIcon(),
            newValueControl,
          ],
        }).addStyleClass("audit-log-item")
      );
    }
  }

  private createArrowIcon() {
    return new Icon({
      src: "sap-icon://arrow-right",
      size: "0.8rem",
    }).addStyleClass("sapUiSmallMarginBeginEnd");
  }

  private getLogFormatter(
    logData: AuditLogDto,
    controller: BaseController
  ): ILogFormatter {
    switch (logData.entityType) {
      case "WorkOrder":
      case "SapSyncWorkOrder":
        return new WorkOrderLogFormatter(controller);
      case "ServicePrice":
        return new ServicePriceLogFormatter(controller);
      case "Specification":
        return new SpecificationLogFormatter(controller);
      case "Contract":
        return new ContractLogFormatter(controller);
      default:
        return new BaseLogFormatter(controller);
    }
  }
}
